import {Map, List} from 'immutable'
import {useState, Fragment, useEffect} from 'react'
import PropTypes from 'prop-types'

import Logger from '../../../../../lib/NewLogger'
import {ModalHeader} from '../../../../../shared_components/core/modals/ModalHeaders'
import Modals from '../../../../../shared_components/core/modals/Modals'
import Closer from '../../../../../shared_components/core/closer/Closer'
import ManageExistingPermissions from './ManageExistingPermissions'
import ManageSelectedDeputiesPermission from './ManageSelectedDeputiesPermission'
import NoExistingPermissions from './NoExistingPermissions'
import SelectDeputies from './SelectDeputies'

import {formatExistingDeputiesPermission, formatSelectedDeputiesPermission} from '../../../../../lib/deputyPermissionTools'
import MixpanelLogger from '../../../../../lib/MixpanelLogger'
import {itemResponseGroupOwners} from '../../../../../lib/plan_data/itemResponsesHelper'


import './managePermissionsModal.scss'

const ManagePermissionsModal = props => {
  const initialSelectedDeputies = props.initialSelectedDeputies || List()
  const [selectedDeputies, setSelectedDeputies] = useState(initialSelectedDeputies)

  // The modal stays mounted while the suggestions are on screen, so a deputy picked from them
  // arrives after mount and is added to whoever is already selected.
  useEffect(() => {
    setSelectedDeputies(existingSelectedDeputies => (
      initialSelectedDeputies.reduce(
        (collection, deputy) => (collection.includes(deputy) ? collection : collection.push(deputy)),
        existingSelectedDeputies
      )
    ))
  }, [props.initialSelectedDeputies])

  const updateExistingPermissions = newPermissions => {
    const updatedDeputiesPermissions = formatExistingDeputiesPermission({newPermissions, deputiesWithPermission: props.deputiesWithPermission})

    props.updatePermissions(updatedDeputiesPermissions)
  }

  const updatePermissionsInBulk = ({selectedPermissionType}) => {
    const {itemResponse, deputiesWithPermission, firstGroupId} = props

    const updatedDeputiesPermissions = formatSelectedDeputiesPermission({
      deputiesWithPermission,
      firstGroupId,
      itemResponse,
      selectedDeputies,
      selectedPermissionType,
      vaultExtension: props.vaultExtension
    })

    props.updatePermissions(updatedDeputiesPermissions)
  }

  const addNewDeputyLink = (contact = Map()) => {
    Logger.log({
      name: 'clicked_add_new_deputy',
      payload: {
        context: 'deputy_permissions_single_item',
        item: props.itemResponse?.get('name') || 'My Prearrangements'
      }
    })

    // A suggested-deputy invite already tracks this event with source 'suggested'
    // before calling here, so only the plain "Add a new Deputy" link tracks its own.
    if (contact.isEmpty()) {
      MixpanelLogger.track(
        'click_add_deputy',
        {context: props.context, deputy_count: props.deputies.size, source: 'planholder'}
      )
    }

    props.openAddDeputyModal(contact)
    props.toggleModal()
  }

  const sharedProps = {
    ...props,
    addNewDeputyLink,
    selectedDeputies,
    setSelectedDeputies
  }

  const modalSubheading = props.itemResponse?.get('is-household') ?
    `Belongs to ${itemResponseGroupOwners(props.responseGroup)}` :
    null

  return (
    <Modals.PopUpModalLarge className='manage-permissions-modal' showModal closerComponent={() => <Closer closer={props.toggleModal} />}>
      {
        <Fragment>
          <ModalHeader
            heading={props.keyField}
            banner={'Permissions'}
            subheading={modalSubheading}
          />
          {
            props.hasNoPermissions ?
              <NoExistingPermissions {...sharedProps} onSubmit={updatePermissionsInBulk} /> :
              <Fragment>
                <SelectDeputies {...sharedProps} />
                {
                  selectedDeputies.isEmpty() ?
                    <ManageExistingPermissions {...sharedProps} onSubmit={updateExistingPermissions} /> :
                    <ManageSelectedDeputiesPermission {...sharedProps} onSubmit={updatePermissionsInBulk} />
                }
              </Fragment>
          }
        </Fragment>
      }
    </Modals.PopUpModalLarge>
  )
}

ManagePermissionsModal.propTypes = {
  context: PropTypes.string,
  deputies: PropTypes.instanceOf(List),
  deputiesWithPermission: PropTypes.instanceOf(List),
  firstGroupId: PropTypes.string,
  hasNoPermissions: PropTypes.bool,
  initialSelectedDeputies: PropTypes.instanceOf(List),
  itemResponse: PropTypes.instanceOf(Map),
  keyField: PropTypes.instanceOf(List),
  openAddDeputyModal: PropTypes.func,
  responseGroup: PropTypes.instanceOf(Map),
  toggleModal: PropTypes.func,
  updatePermissions: PropTypes.func,
  vaultExtension: PropTypes.instanceOf(Map)
}

export default ManagePermissionsModal
